import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown, Check } from "lucide-react";

const LANGUAGES = [
  { code: "", label: "English", short: "EN" },
  { code: "es", label: "Español", short: "ES" },
  { code: "zh-CN", label: "中文（简体）", short: "简" },
  { code: "zh-TW", label: "中文（繁體）", short: "繁" },
  { code: "ko", label: "한국어", short: "KO" },
  { code: "fa", label: "فارسی", short: "FA" },
  { code: "sv", label: "Svenska", short: "SV" },
  { code: "ja", label: "日本語", short: "JA" },
  { code: "fr", label: "Français", short: "FR" },
];

const getCombo = () => document.querySelector("#google_translate_element select.goog-te-combo");

export const LanguageSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [current, setCurrent] = useState(LANGUAGES[0]);
  const wrapRef = useRef(null);

  useEffect(() => {
    window.googleTranslateElementInit = () => {
      if (!window.google?.translate) return;
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: "es,zh-CN,zh-TW,ko,fa,sv,ja,fr",
          autoDisplay: false,
        },
        "google_translate_element"
      );
    };
    if (!document.getElementById("google-translate-script")) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    }
    const timer = setInterval(() => {
      if (getCombo()) {
        setReady(true);
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const pick = (lang) => {
    if (lang.code === "") {
      const parts = window.location.hostname.split(".");
      const domains = [""];
      for (let i = 0; i < parts.length - 1; i++) {
        domains.push(parts.slice(i).join("."));
      }
      domains.forEach((d) => {
        const domainAttr = d ? `; domain=${d}` : "";
        document.cookie = `googtrans=${domainAttr}; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
        document.cookie = `googtrans=${d ? `; domain=.${d}` : ""}; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
      });
      window.location.reload();
      return;
    }
    const combo = getCombo();
    if (!combo) return;
    combo.value = lang.code;
    combo.dispatchEvent(new Event("change", { bubbles: true }));
    setCurrent(lang);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative notranslate">
      <div id="google_translate_element" aria-hidden="true" className="gt-host" />
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={!ready}
        data-testid="language-switcher-button"
        aria-label="Choose language"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-bold tracking-wide hover:border-cyan-400 hover:shadow-[0_0_12px_rgba(0,240,255,0.35)] transition-[border-color,box-shadow] disabled:opacity-50"
      >
        <Globe size={14} className="text-cyan-500" />
        <span className="hidden sm:inline">{current.label}</span>
        <span className="sm:hidden">{current.short}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            data-testid="language-switcher-menu"
            className="absolute right-0 top-11 z-[70] min-w-[180px] bg-white border border-slate-200 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.12),0_0_20px_rgba(0,240,255,0.12)] p-1.5"
          >
            {LANGUAGES.map((lang) => (
              <li key={lang.code || "en"}>
                <button
                  onClick={() => pick(lang)}
                  data-testid={`language-option-${lang.code || "en"}`}
                  className={`w-full flex items-center justify-between gap-3 px-3.5 py-2 rounded-xl text-sm font-semibold text-left transition-colors ${
                    current.code === lang.code
                      ? "text-pink-500 bg-pink-50"
                      : "text-slate-700 hover:bg-cyan-50 hover:text-cyan-600"
                  }`}
                >
                  {lang.label}
                  {current.code === lang.code && <Check size={14} />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
